import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Button } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  isLockReason,
  LOCK_REASON_LABELS,
  LOCK_REASONS,
  type LockReason,
} from "../lib/lockReasons";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * Lock/unlock an account against spending and top-ups. Disputes, SEON and identity verification
 * lock automatically with their own reason; this is the manual counterpart, and it names a
 * reason from the same fixed list plus an optional note. Editors and admins only.
 */
export function LockControl({ endUserId, lockedAt }: { endUserId: string; lockedAt: unknown }) {
  const { can } = useTenantRole();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [reasonCode, setReasonCode] = useState<LockReason | null>(null);
  const [note, setNote] = useState("");

  const setLock = useMutation(
    trpc.users.setLock.mutationOptions({
      onSuccess: (result) => {
        toast.success(result.lockedAt ? "Account locked." : "Account unlocked.");
        setReasonCode(null);
        setNote("");
        void queryClient.invalidateQueries({ queryKey: trpc.users.pathKey() });
      },
    }),
  );

  if (!can("editor")) return null;

  // Distinct keys: the two dialogs sit at the same tree position, and without them React keeps
  // the first dialog's open state when the lock flips, so the other one pops open on its own.
  if (lockedAt) {
    return (
      <AlertDialog key="unlock">
        <AlertDialogTrigger
          render={<Button type="button" variant="outline" size="sm" disabled={setLock.isPending} />}
        >
          Unlock account
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlock this account?</AlertDialogTitle>
            <AlertDialogDescription>
              The shopper can open boxes, claim reveals, and top up their balance again. A lock set
              by a dispute, a fraud screen or identity verification does not come back on its own.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => setLock.mutate({ endUserId, locked: false })}>
              Unlock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog key="lock">
      <AlertDialogTrigger
        render={<Button type="button" variant="outline" size="sm" disabled={setLock.isPending} />}
      >
        Lock account
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Lock this account?</AlertDialogTitle>
          <AlertDialogDescription>
            The shopper keeps their balance and sign-in, but opens, claims, and top-ups are refused
            until an editor unlocks the account.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-2">
            <Label htmlFor="lock-reason">Reason</Label>
            <Select
              value={reasonCode}
              // Base UI renders the raw value in the trigger unless it knows the labels.
              items={LOCK_REASON_LABELS}
              onValueChange={(value) => setReasonCode(isLockReason(value) ? value : null)}
            >
              <SelectTrigger id="lock-reason" className="w-full" aria-label="Lock reason">
                <SelectValue placeholder="Choose a reason" />
              </SelectTrigger>
              <SelectContent>
                {LOCK_REASONS.map((code) => (
                  <SelectItem key={code} value={code}>
                    {LOCK_REASON_LABELS[code]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="lock-note">Note (optional)</Label>
            <Input
              id="lock-note"
              maxLength={512}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!reasonCode}
            onClick={() => {
              if (!reasonCode) return;
              setLock.mutate({
                endUserId,
                locked: true,
                reasonCode,
                note: note.trim() || undefined,
              });
            }}
          >
            Lock account
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
